import React, { useState } from 'react';
import { useAdminStore, AdminTab } from '../store/adminStore';
import { useGameStore } from '../store/gameStore';
import { PrimitivesSelector } from './PrimitivesSelector';
import { CitationAuditDashboard } from './CitationAuditDashboard';
import { ScenarioBuilderWizard } from './ScenarioBuilderWizard';
import { 
  Settings, 
  Map as MapIcon, 
  FileText, 
  Users, 
  BookOpen, 
  BarChart2, 
  X, 
  Save, 
  Download, 
  Upload, 
  Plus, 
  Trash2,
  Edit2,
  Play,
  GraduationCap,
  Database,
  Clock
} from 'lucide-react';
import clsx from 'clsx';

export const GameManagerDashboard: React.FC = () => {
  const { 
    isOpen, 
    toggleDashboard, 
    activeTab, 
    setTab,
    masteryThreshold,
    setMasteryThreshold,
    difficultyPreset,
    setDifficulty,
    sectorConfig,
    updateSectorConfig,
    scenarios,
    addScenario,
    deleteScenario,
    exportScenarios,
    importScenarios
  } = useAdminStore();

  const startGame = useGameStore(state => state.startGame);
  const [showWizard, setShowWizard] = useState(false);

  if (!isOpen) return null;

  if (showWizard) {
    return (
      <div className="fixed inset-0 z-50 bg-slate-950/95 backdrop-blur-sm flex items-center justify-center p-8">
        <div className="w-full max-w-4xl h-[90vh]">
          <ScenarioBuilderWizard 
            onSave={(scenario) => {
              addScenario(scenario);
              setShowWizard(false);
            }}
            onCancel={() => setShowWizard(false)}
          />
        </div>
      </div>
    );
  }

  const tabs: { id: AdminTab; label: string; icon: React.ReactNode }[] = [
    { id: 'general', label: 'General', icon: <Settings size={18} /> },
    { id: 'training', label: 'Training', icon: <GraduationCap size={18} /> },
    { id: 'citations', label: 'Citations', icon: <Database size={18} /> },
    { id: 'sectors', label: 'Sectors', icon: <MapIcon size={18} /> },
    { id: 'scenarios', label: 'Scenarios', icon: <FileText size={18} /> },
    { id: 'teams', label: 'Teams', icon: <Users size={18} /> },
    { id: 'reference', label: 'Reference', icon: <BookOpen size={18} /> },
    { id: 'analytics', label: 'Analytics', icon: <BarChart2 size={18} /> },
  ];

  const handleDifficultyChange = (preset: 'Easy' | 'Normal' | 'Hard' | 'Custom') => {
    setDifficulty(preset);
    if (preset === 'Easy') setMasteryThreshold(5.0);
    if (preset === 'Normal') setMasteryThreshold(7.0);
    if (preset === 'Hard') setMasteryThreshold(8.5);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-slate-950/95 backdrop-blur-sm flex items-center justify-center p-8" onClick={(e) => {
      if (e.target === e.currentTarget) toggleDashboard();
    }}>
      <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-6xl h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-800 bg-slate-900/50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-ops-blue/20 rounded-lg">
              <Settings className="text-ops-blue" size={24} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-100">Game Manager</h2>
              <p className="text-sm text-slate-400">Admin Dashboard & Configuration Tool</p>
            </div>
          </div>
          <button 
            onClick={toggleDashboard}
            className="p-2 hover:bg-slate-800 rounded-full transition-colors text-slate-400 hover:text-slate-200"
          >
            <X size={24} />
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar */}
          <div className="w-64 border-r border-slate-800 bg-slate-900/30 flex flex-col">
            <nav className="flex-1 p-4 space-y-2">
              {tabs.map(tab => {
                const isPlaceholder = ['teams', 'reference', 'analytics'].includes(tab.id);
                return (
                  <button
                    key={tab.id}
                    onClick={() => setTab(tab.id)}
                    className={clsx(
                      'w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all relative',
                      activeTab === tab.id 
                        ? 'bg-ops-blue text-white shadow-lg shadow-ops-blue/20' 
                        : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
                    )}
                  >
                    {tab.icon}
                    <span className="font-medium">{tab.label}</span>
                    {isPlaceholder && (
                      <span className="absolute right-2 text-[10px] bg-slate-800 text-slate-500 px-1.5 py-0.5 rounded border border-slate-700">
                        SOON
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>
            
            <div className="p-4 border-t border-slate-800">
              <div className="text-xs font-mono text-slate-400 text-center bg-slate-800/50 py-2 rounded border border-slate-700/50 hover:bg-slate-800 hover:text-slate-300 transition-colors cursor-help">
                v0.3.9-alpha • <span className="text-ops-blue">Build 2026.02.11</span>
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 overflow-y-auto bg-slate-900/50 p-8">
            {activeTab === 'general' && (
              <div className="space-y-8 max-w-3xl">
                <section>
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <BarChart2 size={20} className="text-ops-blue" />
                    Global Difficulty
                  </h3>
                  <div className="grid grid-cols-4 gap-4">
                    {['Easy', 'Normal', 'Hard', 'Custom'].map((preset) => (
                      <button
                        key={preset}
                        onClick={() => handleDifficultyChange(preset as any)}
                        className={clsx(
                          'p-4 rounded-lg border text-center transition-all',
                          difficultyPreset === preset
                            ? 'bg-ops-blue/20 border-ops-blue text-white'
                            : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600'
                        )}
                      >
                        <div className="font-bold">{preset}</div>
                      </button>
                    ))}
                  </div>
                </section>

                <section>
                  <h3 className="text-lg font-bold text-white mb-4">Mastery Threshold</h3>
                  <div className="bg-slate-800 p-6 rounded-lg border border-slate-700">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-slate-300">Required Score</span>
                      <span className="text-2xl font-bold text-ops-blue">{masteryThreshold}</span>
                    </div>
                    <input
                      type="range"
                      min="1"
                      max="10"
                      step="0.5"
                      value={masteryThreshold}
                      onChange={(e) => setMasteryThreshold(parseFloat(e.target.value))}
                      className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-ops-blue"
                    />
                    <p className="mt-2 text-sm text-slate-500">
                      Minimum trust score required to achieve sector dominance.
                    </p>
                  </div>
                </section>
              </div>
            )}

            {activeTab === 'training' && (
              <PrimitivesSelector />
            )}

            {activeTab === 'citations' && (
              <CitationAuditDashboard />
            )}

            {activeTab === 'sectors' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-bold text-white">Sector Configuration</h3>
                  <button className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg text-white transition-colors">
                    <Plus size={16} /> Add Sector
                  </button>
                </div>
                
                <div className="grid gap-4">
                  {sectorConfig.map(sector => (
                    <div key={sector.id} className="bg-slate-800 p-4 rounded-lg border border-slate-700 flex justify-between items-center">
                      <div>
                        <div className="font-bold text-white">{sector.name}</div>
                        <div className="text-sm text-slate-400">{sector.type} • Trust: {sector.trustScore}/{sector.maxTrust}</div>
                      </div>
                      <div className="flex gap-2">
                        <button className="p-2 hover:bg-slate-700 rounded text-slate-400 hover:text-white">
                          <Edit2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'scenarios' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-bold text-white">Mission Scenarios</h3>
                  <div className="flex gap-2">
                    <button 
                      onClick={() => {
                        const json = exportScenarios();
                        const blob = new Blob([json], { type: 'application/json' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = 'scenarios.json';
                        a.click();
                      }}
                      className="flex items-center gap-2 px-3 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg text-slate-300 transition-colors"
                    >
                      <Download size={16} /> Export
                    </button>
                    <button className="flex items-center gap-2 px-3 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg text-slate-300 transition-colors">
                      <Upload size={16} /> Import
                    </button>
                    <button 
                      onClick={() => setShowWizard(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-ops-blue hover:bg-blue-600 rounded-lg text-white transition-colors"
                    >
                      <Plus size={16} /> New Scenario
                    </button>
                  </div>
                </div>

                <div className="grid gap-4">
                  {scenarios.map(scenario => (
                    <div key={scenario.id} className="bg-slate-800 p-6 rounded-lg border border-slate-700 group hover:border-ops-blue/50 transition-all">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h4 className="text-lg font-bold text-white group-hover:text-ops-blue transition-colors">
                            {scenario.name}
                          </h4>
                          <p className="text-slate-400 mt-1">{scenario.description}</p>
                        </div>
                        <div className="flex gap-2">
                          <button 
                            onClick={() => startGame(scenario.id)}
                            className="p-2 bg-green-500/10 text-green-400 hover:bg-green-500/20 rounded transition-colors"
                            title="Play Scenario"
                          >
                            <Play size={18} />
                          </button>
                          <button className="p-2 hover:bg-slate-700 rounded text-slate-400 hover:text-white">
                            <Edit2 size={18} />
                          </button>
                          <button 
                            onClick={() => deleteScenario(scenario.id)}
                            className="p-2 hover:bg-red-500/10 rounded text-slate-400 hover:text-red-400"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                      
                      <div className="flex gap-4 text-sm text-slate-500">
                        <div className="flex items-center gap-1">
                          <Clock size={14} />
                          {scenario.turnLimit} Turns • {scenario.difficulty}
                        </div>
                        <div className="flex items-center gap-1">
                          <MapIcon size={14} />
                          {scenario.targetSectorIds?.length ?? 0} Sectors
                        </div>
                        <div className="flex items-center gap-1">
                          <Users size={14} />
                          {scenario.objectives?.length ?? 0} Objectives
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Placeholders for other tabs */}
            {['teams', 'reference', 'analytics'].includes(activeTab) && (
              <div className="flex flex-col items-center justify-center h-full text-slate-500">
                <Settings size={48} className="mb-4 opacity-20" />
                <p className="text-lg">This module is under construction.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
